/**
 * Plugin Error Boundary — Wave 22.13.9
 *
 * React error boundary that catches UI exceptions from plugin components.
 * Shows a fallback UI, logs the error to plugin diagnostics, records
 * the error in the stability store for DevTools, and prevents the host
 * React tree from crashing.
 *
 * Usage:
 * ```tsx
 * <PluginErrorBoundary pluginId="my-plugin">
 *   <PluginComponent />
 * </PluginErrorBoundary>
 * ```
 */

import React from 'react';
import { usePluginStabilityStore } from './plugin-stability-store';
import { usePluginLogStore } from './plugin-log-store';
import { reloadForChunkLoadError } from '../../utils/chunkLoadRecovery';

// ────────────────────────────────────────────────────────
// P1-1 Fix: Bridge SDK-side PluginUIErrorBoundary events
// into the host stability store.  The SDK boundary emits
// `neuro:plugin-ui-error` CustomEvents on `window` — we
// listen for them here and forward to the stability store
// so that SDK-caught errors are reflected in crash
// recovery and DevTools scoring.
// ────────────────────────────────────────────────────────

let sdkErrorListenerInstalled = false;

function installSdkErrorBridge(): void {
  if (sdkErrorListenerInstalled || typeof window === 'undefined') return;
  sdkErrorListenerInstalled = true;

  window.addEventListener('neuro:plugin-ui-error', ((event: CustomEvent) => {
    try {
      const detail = event.detail;
      if (!detail?.pluginId) return;

      const stabilityStore = usePluginStabilityStore.getState();
      stabilityStore.recordEvent(
        detail.pluginId,
        'ui-error',
        detail.error?.message ?? 'SDK UI error',
        {
          componentName: detail.componentName,
          componentStack: detail.componentStack,
          stack: detail.error?.stack,
          source: 'plugin-ui-sdk',
        },
      );

      const logStore = usePluginLogStore.getState();
      logStore.appendLog(
        detail.pluginId,
        'error',
        `SDK UI Error${detail.componentName ? ` in ${detail.componentName}` : ''}: ${detail.error?.message ?? 'unknown'}`,
        'PluginUIErrorBoundary',
      );
    } catch {
      /* best-effort — never crash the host over telemetry */
    }
  }) as EventListener);
}

// Install immediately on module load
installSdkErrorBridge();

// ────────────────────────────────────────────────────────
// Types
// ────────────────────────────────────────────────────────

export interface PluginErrorBoundaryProps {
  pluginId: string;
  children: React.ReactNode;
  /** Optional custom fallback UI. */
  fallback?: React.ReactNode;
  /** Called when an error is caught. */
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

interface PluginErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

// ────────────────────────────────────────────────────────
// Component
// ────────────────────────────────────────────────────────

export class PluginErrorBoundary extends React.Component<
  PluginErrorBoundaryProps,
  PluginErrorBoundaryState
> {
  constructor(props: PluginErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): Partial<PluginErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    const { pluginId, onError } = this.props;

    // A chunk left behind by a deploy isn't a plugin crash — reload onto the
    // current build instead of counting it toward crash-recovery disable.
    if (reloadForChunkLoadError(error)) return;

    // Record in stability store
    const stabilityStore = usePluginStabilityStore.getState();
    stabilityStore.recordEvent(pluginId, 'ui-error', error.message, {
      componentStack: errorInfo.componentStack ?? undefined,
      stack: error.stack ?? undefined,
    });

    // Record in plugin log store
    const logStore = usePluginLogStore.getState();
    logStore.appendLog(pluginId, 'error', `UI Error: ${error.message}`, 'PluginErrorBoundary');

    // Track crash count  
    stabilityStore.recordCrash(pluginId, `UI crash: ${error.message}`, {
      componentStack: errorInfo.componentStack ?? undefined,
    });

    // Emit to plugin event bus (best-effort, fire-and-forget)
    void import('../plugin-event-bus').then(({ usePluginEventBusStore }) => {
      usePluginEventBusStore.getState().emit('system', 'plugin.ui.error', {
        pluginId,
        error: error.message,
        componentStack: errorInfo.componentStack,
        timestamp: new Date().toISOString(),
      });
    }).catch((err) => { console.warn('Plugin error boundary event bus notification failed:', err); });

    // Console error for DevTools
    console.error(
      `[PluginErrorBoundary] UI error in plugin "${pluginId}":`,
      error,
      errorInfo.componentStack,
    );

    this.setState({ errorInfo });
    onError?.(error, errorInfo);
  }

  private handleRetry = (): void => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render(): React.ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return React.createElement(
        'div',
        {
          role: 'alert',
          style: {
            padding: 'var(--nc-space-md, 16px)',
            margin: 'var(--nc-space-sm, 8px)',
            borderRadius: 'var(--nc-radius-md, 8px)',
            background: 'var(--nc-color-error-bg, rgba(239, 68, 68, 0.1))',
            border: '1px solid var(--nc-color-error, rgba(239, 68, 68, 0.3))',
            color: 'var(--nc-color-text, #fff)',
            fontSize: 'var(--nc-size-sm, 0.875rem)',
          },
        },
        React.createElement(
          'div',
          {
            style: {
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--nc-space-sm, 8px)',
              marginBottom: 'var(--nc-space-sm, 8px)',
            },
          },
          React.createElement('span', { style: { fontSize: '1.25rem' } }, '⚠'),
          React.createElement(
            'strong',
            null,
            `Plugin "${this.props.pluginId}" encountered an error`,
          ),
        ),
        React.createElement(
          'p',
          {
            style: {
              margin: '0 0 var(--nc-space-sm, 8px) 0',
              color: 'var(--nc-color-text-muted, #888)',
            },
          },
          this.state.error?.message ?? 'Unknown error',
        ),
        React.createElement(
          'button',
          {
            onClick: this.handleRetry,
            style: {
              background: 'var(--nc-color-primary, #6366f1)',
              color: '#fff',
              border: 'none',
              borderRadius: 'var(--nc-radius-sm, 4px)',
              padding: 'var(--nc-space-xs, 4px) var(--nc-space-md, 16px)',
              cursor: 'pointer',
              fontSize: 'var(--nc-size-sm, 0.875rem)',
            },
          },
          'Retry',
        ),
      );
    }

    return this.props.children;
  }
}
